// src/layout/partials/menu.jsx
import React, { useState, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'antd';
import {
  HomeOutlined,
  AppstoreOutlined,
  UserOutlined,
  HistoryOutlined,
  SwapOutlined,
  CustomerServiceOutlined,
  SettingOutlined,
  WalletOutlined,
  TrophyOutlined,
  BankOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CarOutlined,
  FileTextOutlined,
  TeamOutlined,
  DollarOutlined,
  AuditOutlined,
  SyncOutlined,
  PictureOutlined,
  UsergroupAddOutlined,
  FlagOutlined,
  MoneyCollectOutlined,
  GlobalOutlined,
  RocketOutlined
} from '@ant-design/icons';
import { AuthContext } from '../../../common/constants';
import { useLayout } from '../LayoutContext';
import Logo from '../../../layout/logo/tigoon-connect-logo.png';
import './menu.scss';

// Rôles ayant accès complet à tout
const FULL_ACCESS_ROLES = ['superAdmin', 'directeur', 'support'];

const MenuComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { sidebarOpen, isXLUp, closeSidebar, isCollapsed, toggleCollapsed } = useLayout();
  const { currentUser } = useContext(AuthContext);

  const [openKeys, setOpenKeys] = useState([]);

  // Fonction pour vérifier si l'utilisateur a une permission
  const hasPerm = (perm) => {
    if (!perm) return true;
    if (FULL_ACCESS_ROLES.includes(currentUser?.role)) return true;
    const perms = new Set(currentUser?.permissions || []);
    return perms.has(perm);
  };

  // Fonction pour vérifier si l'utilisateur a au moins une des permissions
  const hasAnyPerm = (anyOf) => {
    if (!anyOf || anyOf.length === 0) return true;
    if (FULL_ACCESS_ROLES.includes(currentUser?.role)) return true;
    const perms = new Set(currentUser?.permissions || []);
    return anyOf.some(p => perms.has(p));
  };

  // Fonction pour vérifier l'accès d'un item (supporte perm et anyOf)
  const hasAccess = (item) => {
    if (!item) return false;
    if (item.anyOf) return hasAnyPerm(item.anyOf);
    if (item.perm) return hasPerm(item.perm);
    return true;
  };

  // Fonction pour vérifier si un item ou ses enfants sont accessibles
  const isItemVisible = (item) => {
    if (!item) return false;
    if (item.children) {
      return item.children.some(child => hasAccess(child));
    }
    return hasAccess(item);
  };

  // Déterminer la clé sélectionnée basée sur le path
  const getSelectedKey = () => {
    if (currentPath === '/dashboard') return 'dashboard';
    // Module MotoLivraison — testé EN PREMIER : sinon '/motolivraison/categories'
    // serait capté par la règle générique '/categories' plus bas et c'est l'entrée
    // « Services & catégories » qui s'allumerait à la place.
    if (currentPath.includes('/motolivraison/categories')) return 'motoliv-categories';
    if (currentPath.includes('/motolivraison/commercants')) return 'motoliv-commercants';
    if (currentPath.includes('/motolivraison/commerces')) return 'motoliv-commerces';
    if (currentPath.includes('/motolivraison/livreurs')) return 'motoliv-livreurs';
    if (currentPath.includes('/motolivraison/commandes')) return 'motoliv-commandes';
    if (currentPath.includes('/motolivraison/reversements')) return 'motoliv-reversements';
    if (currentPath.includes('/categories')) return 'categories-list';
    if (currentPath.includes('/ajouter-categorie')) return 'categories-add';
    if (currentPath.includes('/partenaires')) return 'inscription-tigoon';
    if (currentPath.includes('/vehicules')) return 'inscription-vehicules';
    if (currentPath.includes('/chauffeurs')) return 'inscription-yandex';
    if (currentPath.includes('/historique-numeros')) return 'histo-numeros';
    if (currentPath.includes('/historique-vehicules')) return 'histo-vehicules';
    if (currentPath.includes('/historique-transferts')) return 'trans-transferts';
    if (currentPath.includes('/historique-sos')) return 'trans-sos';
    if (currentPath.includes('/demandes-assistances')) return 'assistance-demandes';
    if (currentPath.includes('/gestion-admins')) return 'admin-list';
    if (currentPath.includes('/ajouter-admin')) return 'admin-add';
    if (currentPath.includes('/roles')) return 'roles-list';
    if (currentPath.includes('/ajouter-role')) return 'roles-add';
    if (currentPath.includes('/modifier-role')) return 'roles-list';
    if (currentPath.includes('/ajustements-admin')) return 'wallet-ajustements';
    if (currentPath.includes('/reporting-fraude')) return 'wallet-reporting-fraude';
    if (currentPath.includes('/audit-fraude')) return 'wallet-audit-fraude';
    if (currentPath.includes('/wallet')) return 'wallet-main';
    if (currentPath.includes('/historique-points')) return 'wallet-points';
    if (currentPath.includes('/historique-promotions')) return 'promo-dimanche';
    if (currentPath.includes('/toutes-operations')) return 'promo-operations';
    if (currentPath.includes('/sites')) return 'terrain-sites';
    if (currentPath.includes('/ajout-site')) return 'terrain-ajout';
    if (currentPath.includes('/souscripteurs')) return 'terrain-souscripteurs';
    if (currentPath.includes('/settings')) return 'settings';
    if (currentPath.includes('/sync-chauffeurs')) return 'sync-chauffeurs';
    if (currentPath.includes('/sync-vehicules')) return 'sync-vehicules';
    if (currentPath.includes('/sync-anciens-chauffeurs')) return 'sync-anciens-chauffeurs';
    if (currentPath.includes('/sync-cache-yandex')) return 'sync-cache-yandex';
    if (currentPath.includes('/banners')) return 'banners';
    if (currentPath.includes('/vehicle-service-classes')) return 'vehicle-service-classes';
    if (currentPath.includes('/vehicle-brands')) return 'vehicle-brands';
    if (currentPath.includes('/affiliates') && currentPath.includes('/detail')) return 'affiliates-list';
    if (currentPath.includes('/affiliates')) return 'affiliates-list';
    if (currentPath.includes('/affiliate-applications')) return 'affiliates-applications';
    if (currentPath.includes('/affiliate-commissions')) return 'commissions-list';
    if (currentPath.includes('/commissions-130h')) return 'commissions-130h';
    if (currentPath.includes('/ambassadors') && currentPath.includes('/detail')) return 'ambassadors-list';
    if (currentPath.includes('/ambassadors')) return 'ambassadors-list';
    if (currentPath.includes('/ambassador-applications')) return 'ambassadors-applications';
    if (currentPath.includes('/revenues-monthly-retentions')) return 'revenues-monthly-retentions';
    if (currentPath.includes('/revenues-bonus-recharge')) return 'revenues-bonus-recharge';
    if (currentPath.includes('/revenues-130h')) return 'revenues-130h';
    if (currentPath.includes('/revenues-payment-status')) return 'revenues-payment-status';
    if (currentPath.includes('/revenues')) return 'revenues-list';
    if (currentPath.includes('/quarterly-rankings')) return 'rankings-quarterly';
    if (currentPath.includes('/rankings')) return 'rankings-monthly';
    if (currentPath.includes('/cities')) return 'cities';
    if (currentPath.includes('/all-users')) return 'all-users';
    if (currentPath.includes('/audit-log')) return 'audit-log';
    if (currentPath.includes('/motolivraison')) return 'motoliv-dashboard';
    return 'dashboard';
  };

  const handleMenuClick = ({ key }) => {
    const routes = {
      'dashboard': '/dashboard',
      'categories-list': '/dashboard/categories',
      'categories-add': '/dashboard/ajouter-categorie',
      'inscription-tigoon': '/dashboard/partenaires',
      'inscription-vehicules': '/dashboard/vehicules',
      'inscription-yandex': '/dashboard/chauffeurs',
      'histo-numeros': '/dashboard/historique-numeros',
      'histo-vehicules': '/dashboard/historique-vehicules-modifies',
      'trans-transferts': '/dashboard/historique-transferts',
      'trans-sos': '/dashboard/historique-sos-credit',
      'assistance-demandes': '/dashboard/demandes-assistances',
      'admin-list': '/dashboard/gestion-admins',
      'admin-add': '/dashboard/ajouter-admin',
      'roles-list': '/dashboard/roles',
      'roles-add': '/dashboard/ajouter-role',
      'wallet-main': '/dashboard/wallet',
      'wallet-points': '/dashboard/historique-points',
      'wallet-ajustements': '/dashboard/ajustements-admin',
      'wallet-audit-fraude': '/dashboard/audit-fraude',
      'wallet-reporting-fraude': '/dashboard/reporting-fraude',
      'promo-dimanche': '/dashboard/historique-promotions',
      'promo-operations': '/dashboard/toutes-operations',
      'terrain-sites': '/dashboard/sites',
      'terrain-ajout': '/dashboard/ajout-site',
      'terrain-souscripteurs': '/dashboard/souscripteurs',
      'settings': '/dashboard/settings',
      'sync-chauffeurs': '/dashboard/sync-chauffeurs',
      'sync-vehicules': '/dashboard/sync-vehicules',
      'sync-anciens-chauffeurs': '/dashboard/sync-anciens-chauffeurs',
      'sync-cache-yandex': '/dashboard/sync-cache-yandex',
      'banners': '/dashboard/banners',
      'app-banners': '/dashboard/app-banners',
      'vehicle-brands': '/dashboard/vehicle-brands',
      'vehicle-service-classes': '/dashboard/vehicle-service-classes',
      'affiliates-list': '/dashboard/affiliates',
      'affiliates-applications': '/dashboard/affiliate-applications',
      'commissions-list': '/dashboard/affiliate-commissions',
      'commissions-130h': '/dashboard/commissions-130h',
      'ambassadors-list': '/dashboard/ambassadors',
      'ambassadors-applications': '/dashboard/ambassador-applications',
      'revenues': '/dashboard/revenues',
      'revenues-list': '/dashboard/revenues',
      'revenues-bonus-recharge': '/dashboard/revenues-bonus-recharge',
      'revenues-monthly-retentions': '/dashboard/revenues-monthly-retentions',
      'revenues-130h': '/dashboard/revenues-130h',
      'revenues-payment-status': '/dashboard/revenues-payment-status',
      'rankings-monthly': '/dashboard/rankings',
      'rankings-quarterly': '/dashboard/quarterly-rankings',
      'cities': '/dashboard/cities',
      'all-users': '/dashboard/all-users',
      'audit-log': '/dashboard/audit-log',
      // Module MotoLivraison
      'motoliv-dashboard': '/dashboard/motolivraison',
      'motoliv-categories': '/dashboard/motolivraison/categories',
      'motoliv-commercants': '/dashboard/motolivraison/commercants',
      'motoliv-commerces': '/dashboard/motolivraison/commerces',
      'motoliv-livreurs': '/dashboard/motolivraison/livreurs',
      'motoliv-commandes': '/dashboard/motolivraison/commandes',
      'motoliv-reversements': '/dashboard/motolivraison/reversements',
    };

    if (routes[key]) {
      navigate(routes[key]);
      if (!isXLUp) closeSidebar();
    }
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  // Définition des items par section
  const gestionItems = [
    {
      key: 'categories',
      icon: <AppstoreOutlined />,
      label: 'Gestion Services',
      children: [
        { key: 'categories-list', label: 'Liste', perm: 'categories:read' },
        { key: 'categories-add', label: 'Ajout', perm: 'categories:create' },
      ],
    },
    {
      key: 'inscription',
      icon: <UserOutlined />,
      label: 'Inscription',
      children: [
        { key: 'inscription-tigoon', label: 'TIGOON', perm: 'partners:read' },
        { key: 'inscription-vehicules', label: 'Véhicules', perm: 'vehicles:read' },
        { key: 'inscription-yandex', label: 'YANDEX', perm: 'drivers:read' },
      ],
    },
    {
      key: 'histomodif',
      icon: <HistoryOutlined />,
      label: 'Historique Modif.',
      children: [
        { key: 'histo-numeros', label: 'Numéros', perm: 'userPhone.history:read' },
        { key: 'histo-vehicules', label: 'Véhicules', perm: 'vehicleChanges.history:read' },
      ],
    },
    {
      key: 'transactions',
      icon: <SwapOutlined />,
      label: 'Transactions',
      children: [
        { key: 'trans-transferts', label: 'Transferts UV', perm: 'transfers.history:read' },
        { key: 'trans-sos', label: 'SOS Crédits', perm: 'soscredit.history:read' },
      ],
    },
    {
      key: 'assistance',
      icon: <CustomerServiceOutlined />,
      label: 'Assistance',
      children: [
        { key: 'assistance-demandes', label: 'Mes demandes', anyOf: ['assistance:read', 'assistance:update'] },
      ],
    },
  ];

  const administrationItems = [
    {
      key: 'rankings',
      icon: <TrophyOutlined />,
      label: 'Classement',
      children: [
        { key: 'rankings-monthly', label: 'Classement Mensuel', perm: 'rankings:read' },
        { key: 'rankings-quarterly', label: 'Classement Trimestriel', perm: 'rankings:read' },
      ],
    },
    {
      key: 'revenues',
      icon: <MoneyCollectOutlined />,
      label: 'Revenus',
      children: [
        { key: 'revenues-list', label: 'Liste Revenus', perm: 'affiliates.commissions:read' },
        { key: 'revenues-130h', label: 'Revenus 130h', perm: 'affiliates.commissions:read' },
        { key: 'revenues-bonus-recharge', label: 'Bonus Recharge Jour', perm: 'affiliates.commissions:read' },
        { key: 'revenues-monthly-retentions', label: 'Rétentions Mensuelles', perm: 'affiliates.commissions:read' },
        { key: 'revenues-payment-status', label: 'État Paiements', perm: 'affiliates.commissions:read' },
      ],
    },
    {
      key: 'admin',
      icon: <TeamOutlined />,
      label: 'Administrateurs',
      children: [
        { key: 'admin-list', label: 'Liste', perm: 'users:read' },
        { key: 'admin-add', label: 'Ajout', perm: 'users:create' },
        { key: 'roles-list', label: 'Rôles', perm: 'roles:read' },
        { key: 'roles-add', label: 'Ajout Rôle', perm: 'roles:create' },
        { key: 'audit-log', label: "Historique d'activité", perm: 'audit:read' },
      ],
    },
    {
      key: 'affiliates',
      icon: <UsergroupAddOutlined />,
      label: 'Affiliés',
      children: [
        { key: 'affiliates-list', label: 'Liste', perm: 'affiliates:read' },
        { key: 'affiliates-applications', label: 'Candidatures', perm: 'affiliates.applications:read' },
      ],
    },
    {
      key: 'commissions',
      icon: <DollarOutlined />,
      label: 'Commissions',
      children: [
        { key: 'commissions-list', label: 'Commissions', perm: 'affiliates.commissions:read' },
        { key: 'commissions-130h', label: 'Commission 130h', perm: 'affiliates.commissions:read' },
      ],
    },
    {
      key: 'ambassadors',
      icon: <FlagOutlined />,
      label: 'Ambassadeurs',
      children: [
        { key: 'ambassadors-list', label: 'Liste', perm: 'ambassadors:read' },
        { key: 'ambassadors-applications', label: 'Candidatures', perm: 'ambassadors.applications:read' },
      ],
    },
    {
      key: 'wallet',
      icon: <WalletOutlined />,
      label: 'Wallet',
      children: [
        { key: 'wallet-main', label: 'Wallet', anyOf: ['finance:read', 'finance:update'] },
        { key: 'wallet-points', label: 'Points', perm: 'finance:read' },
        { key: 'wallet-ajustements', label: 'Ajustements Admin', perm: 'finance:read' },
        { key: 'wallet-audit-fraude', label: 'Audit Fraude', perm: 'finance:read' },
        { key: 'wallet-reporting-fraude', label: 'Reporting Fraude', perm: 'finance:read' },
      ],
    },
    {
      key: 'promotion',
      icon: <TrophyOutlined />,
      label: 'Promotion',
      children: [
        { key: 'promo-dimanche', label: 'Promo Dimanche', perm: 'promotions:read' },
        { key: 'promo-operations', label: 'Opérations', perm: 'operations:read' },
      ],
    },
    {
      key: 'terrain',
      icon: <BankOutlined />,
      label: 'Achète ton terrain',
      children: [
        { key: 'terrain-sites', label: 'Sites', perm: 'sites:read' },
        { key: 'terrain-ajout', label: 'Ajout Site', perm: 'sites:create' },
        { key: 'terrain-souscripteurs', label: 'Souscripteurs', perm: 'sites:read' },
      ],
    },
    {
      key: 'motolivraison',
      icon: <RocketOutlined />,
      label: 'MotoLivraison',
      children: [
        { key: 'motoliv-dashboard', label: 'Dashboard', perm: 'motolivraison.stats:read' },
        { key: 'motoliv-categories', label: 'Catégories', perm: 'motolivraison.categories:read' },
        { key: 'motoliv-commercants', label: 'Commerçants', perm: 'motolivraison.commerces:read' },
        { key: 'motoliv-commerces', label: 'Commerces', perm: 'motolivraison.commerces:read' },
        { key: 'motoliv-livreurs', label: 'Livreurs', perm: 'motolivraison.livreurs:read' },
        { key: 'motoliv-commandes', label: 'Commandes', perm: 'motolivraison.commandes:read' },
        { key: 'motoliv-reversements', label: 'Reversements', perm: 'motolivraison.reversements:read' },
      ],
    },
  ];

  const configurationItems = [
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Paramètres',
      perm: 'settings:read',
    },
    {
      key: 'synchronisation',
      icon: <SyncOutlined />,
      label: 'Synchronisation',
      children: [
        { key: 'sync-chauffeurs', label: 'Chauffeurs non liés', perm: 'sync:read' },
        { key: 'sync-vehicules', label: 'Véhicules manquants', perm: 'sync:read' },
        { key: 'sync-anciens-chauffeurs', label: 'Anciens chauffeurs', perm: 'sync:read' },
        { key: 'sync-cache-yandex', label: 'Cache Yandex', perm: 'sync:read' },
      ],
    },
    {
      key: 'banners',
      icon: <PictureOutlined />,
      label: 'Bannières Website',
      perm: 'banners:read',
    },
    {
      key: 'app-banners',
      icon: <PictureOutlined />,
      label: 'Bannières App',
      perm: 'appBanners:read',
    },
    {
      key: 'vehicle-brands',
      icon: <CarOutlined />,
      label: 'Marques & Modèles',
      perm: 'vehicleBrands:read',
    },
    {
      key: 'vehicle-service-classes',
      icon: <AppstoreOutlined />,
      label: 'Classes de service',
      perm: 'vehicleServiceClasses:read',
    },
    {
      key: 'cities',
      icon: <GlobalOutlined />,
      label: 'Villes',
      perm: 'cities:read',
    },
    {
      key: 'all-users',
      icon: <TeamOutlined />,
      label: 'Tous les utilisateurs',
      perm: 'allUsers:read',
    },
  ];

  // Fonction pour filtrer les items d'une section
  const filterSectionItems = (items) => {
    return items
      .map(item => {
        if (item.children) {
          const filteredChildren = item.children.filter(child => hasAccess(child));
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return hasAccess(item) ? item : null;
      })
      .filter(Boolean);
  };

  // Construire le menu final avec sections conditionnelles
  const menuItems = useMemo(() => {
    const items = [];

    // Dashboard (toujours visible si permission)
    const dashboardItem = {
      key: 'dashboard',
      icon: <HomeOutlined />,
      label: 'Tableau de bord',
      perm: 'dashboard:view',
    };
    if (hasAccess(dashboardItem)) {
      items.push(dashboardItem);
    }

    // Section GESTION
    const filteredGestion = filterSectionItems(gestionItems);
    if (filteredGestion.length > 0) {
      if (!isCollapsed) {
        items.push({ type: 'divider' });
        items.push({ key: 'gestion-label', type: 'group', label: 'GESTION' });
      }
      items.push(...filteredGestion);
    }

    // Section ADMINISTRATION
    const filteredAdmin = filterSectionItems(administrationItems);
    if (filteredAdmin.length > 0) {
      if (!isCollapsed) {
        items.push({ type: 'divider' });
        items.push({ key: 'admin-label', type: 'group', label: 'ADMINISTRATION' });
      }
      items.push(...filteredAdmin);
    }

    // Section CONFIGURATION
    const filteredConfig = filterSectionItems(configurationItems);
    if (filteredConfig.length > 0) {
      if (!isCollapsed) {
        items.push({ type: 'divider' });
        items.push({ key: 'config-label', type: 'group', label: 'CONFIGURATION' });
      }
      items.push(...filteredConfig);
    }

    return items;
  }, [currentUser?.permissions, currentUser?.role, isCollapsed]);

  return (
    <aside
      id="layout-menu"
      className={`layout-menu menu-vertical menu ${sidebarOpen ? 'is-open' : ''} ${isXLUp && isCollapsed ? 'is-collapsed' : ''}`}
      aria-hidden={!sidebarOpen && !isXLUp}
    >
      {/* Logo & Brand */}
      <div className="app-brand">
        <div className="brand-content">
          <div className="logo-box">
            <img src={Logo} alt="TIGOON" />
          </div>
          {!isCollapsed && (
            <span className="app-brand-text">
              TIGOON<br />CONNECT
            </span>
          )}
        </div>

        {/* Toggle button */}
        {isXLUp && (
          <button
            className="collapse-btn"
            type="button"
            onClick={toggleCollapsed}
            title={isCollapsed ? 'Agrandir' : 'Réduire'}
          >
            {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        )}

        {/* Close button (mobile) */}
        {!isXLUp && (
          <button
            className="collapse-btn close-btn"
            onClick={closeSidebar}
            type="button"
          >
            ×
          </button>
        )}
      </div>

      {/* Menu Ant Design */}
      <Menu
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        openKeys={isCollapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        items={menuItems}
        inlineCollapsed={isCollapsed}
        className="sidebar-menu"
      />

      {/* Footer */}
      {!isCollapsed && (
        <div className="sidebar-footer">
          <div className="version-badge">
            <span className="status-dot"></span>
            <span>Backoffice v1.0</span>
          </div>
        </div>
      )}
    </aside>
  );
};

export default MenuComponent;
